import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  Checkbox, 
  Button, 
  Space, 
  message, 
  Spin, 
  Alert, 
  Row, 
  Col,
  Input,
  Select,
  Tabs,
  Divider,
  Tag,
  Tooltip,
  Empty,
  Badge,
  Breadcrumb,
  Typography
} from 'antd';
import { 
  SearchOutlined, 
  CheckOutlined, 
  CloseOutlined,
  ShopOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { accountService } from '../../services/accountService';
import { roleService } from '../../services/roleService';
import storeGroupService from '../../services/storeGroupService';

const { Title, Text } = Typography;

/**
 * Account Store Data Permission Authorization Page Component
 * @author AI Assistant
 * @description Independent page component for managing account store data permission assignments
 */
const AccountStoreAuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginId } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [storePermissionList, setStorePermissionList] = useState([]);
   const [storeGroupList, setStoreGroupList] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [originalCheckedKeys, setOriginalCheckedKeys] = useState([]);
  
  // 筛选和搜索状态
  const [searchKeyword, setSearchKeyword] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [storeTypeFilter, setStoreTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // 选项卡状态
  const [activeTab, setActiveTab] = useState('individual'); // individual | group
  
  // 门店组选择状态
  const [selectedStoreGroups, setSelectedStoreGroups] = useState([]);
  
  // 加载状态
  const [storeGroupLoading, setStoreGroupLoading] = useState(false);

  // Load account info and data when page loads
  useEffect(() => {
    if (loginId) {
       loadData();
    }
  }, [loginId]);

  // 监听Tab切换，当切换到门店组Tab时加载数据
  useEffect(() => {
    if (activeTab === 'group' && storeGroupList.length === 0) {
      loadStoreGroups();
    }
  }, [activeTab]);

  const loadData = async () => {
      await loadAccountInfo();
      await loadStorePermissionData();
      await loadAccountStorePermissions();
  };

  // Load account information
  const loadAccountInfo = async () => {
    try {
      // 这里可以调用获取账号详情的API
      // 暂时使用loginId作为基本信息
      setAccount({ loginId, name: loginId });
    } catch (error) {
      console.error('加载账号信息失败:', error);
      message.error(t('account.loadAccountInfoFailed'));
    }
  };

  // 加载门店组数据
  const loadStoreGroups = async () => {
    setStoreGroupLoading(true);
    try {
      console.log('开始加载门店组数据...');
      const response = await storeGroupService.getStoreGroups({ pageSize: 1000 });
      console.log('门店组数据响应:', response);
      if (response.success && response.data) {
        const storeGroups = response.data.tableData || [];
        console.log('解析到的门店组数据:', storeGroups);
        setStoreGroupList(storeGroups);
      } else {
        console.warn('门店组数据加载失败:', response.errorMessage);
        setStoreGroupList([]);
      }
    } catch (error) {
      console.error('加载门店组数据失败:', error);
      message.error(t('account.loadStoreGroupsFailed'));
      setStoreGroupList([]);
    } finally {
      setStoreGroupLoading(false);
    }
  };

  // 过滤门店权限数据
  const filteredStorePermissions = useMemo(() => {
    let filtered = [...storePermissionList];
    
    // 关键词搜索
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(permission => 
        permission.name.toLowerCase().includes(keyword) ||
        permission.code.toLowerCase().includes(keyword)
      );
    }
    
    return filtered;
  }, [storePermissionList, searchKeyword]);

  const loadStorePermissionData = async () => {
    setLoading(true);
    try {
      const response = await roleService.getRoles({ type:"DATA", pageSize: 1000 });
      if (response.success && response.data) {
        setStorePermissionList(response.data.tableData || []);
      } else {
        message.error(response.errorMessage || t('account.loadStorePermissionsFailed'));
      }
    } catch (error) {
      console.error('加载门店权限数据失败:', error);
      message.error(t('account.loadStorePermissionsFailedRetry'));
    } finally {
      setLoading(false);
    }
  };

  const loadAccountStorePermissions = async () => {
    if (!loginId) return;
    
    console.log('开始加载账号门店权限:', loginId);
    
    try {
      const response = await roleService.getRoles({"type":"DATA","authQueryType":"1", "authedAccount":loginId});
      console.log('账号数据权限角色响应:', response);  
      
      if (response.success && response.data) {
        const permissionCodes = Array.isArray(response.data.tableData) 
          ? response.data?.tableData?.map(permission => permission.code) 
          : [];
        console.log('获取到的门店权限代码:', permissionCodes);
        setCheckedKeys(permissionCodes);
        setOriginalCheckedKeys([...permissionCodes]);
      } else {
        console.warn('获取账号门店权限失败:', response.errorMessage);
        setCheckedKeys([]);
        setOriginalCheckedKeys([]);
      }
    } catch (error) {
      console.error('加载账号门店权限失败:', error);
      setCheckedKeys([]);
      setOriginalCheckedKeys([]);
    }
  };

  const mapStoreToPermission = (storeCode) => {
    return "Store" + storeCode + " Data Permission";
  };
  
  // 门店组批量选择
  const handleStoreGroupSelect = (selectedGroups) => {
    setSelectedStoreGroups(selectedGroups);
    
    if (selectedGroups.length > 0) {
      const relatedPermissions = [];
      
      selectedGroups.forEach(groupCode => {
        const group = storeGroupList.find(g => g.code === groupCode);
        console.log('处理门店组:', { groupCode, group });
        if (group && group.shopCodes) {
          group.shopCodes.forEach(shopCode => {
            const permissionCode = mapStoreToPermission(shopCode);
            console.log('门店映射:', { shopCode, permissionCode });
            if (permissionCode) {
              const permission = storePermissionList.find(p => p.code === permissionCode);
              if (permission) {
                relatedPermissions.push(permission.code);
                console.log('找到权限:', permission.name);
              } else {
                console.warn('未找到权限数据:', permissionCode);
              }
            } else {
              console.warn('未找到权限映射:', shopCode);
            }
          });
        }
      });
      
      const newCheckedKeys = [...new Set([...checkedKeys, ...relatedPermissions])];
      setCheckedKeys(newCheckedKeys);
      
      console.log('门店组选择联动:', {
        selectedGroups,
        relatedPermissions,
        newCheckedKeys,
        currentCheckedKeys: checkedKeys
      });
    }
  };

  // 渲染逐个门店权限选择
  const renderIndividualStoreAuth = () => {
    return (
      <Card>
        {/* 搜索功能 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]}>
            <Col span={16}>
              <Input
                placeholder={t('account.searchStorePermissions')}
                prefix={<SearchOutlined />}
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'right', color: '#666', fontSize: '12px', paddingTop: '6px' }}>
                {t('account.filteredCount', { count: filteredStorePermissions.length })}
              </div>
            </Col>
          </Row>
        </Card>

        {/* 门店权限列表 */}
        <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid #d9d9d9', padding: 16, borderRadius: 6 }}>
          {filteredStorePermissions.length === 0 ? (
            <Empty 
              description={searchKeyword ? t('account.noFilteredResults') : t('account.noStorePermissionsAvailable')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Checkbox.Group
              value={checkedKeys}
              onChange={onCheck}
              style={{ width: '100%' }}
              disabled={saving}
            >
              <Row gutter={[16, 8]}>
                {filteredStorePermissions.map((permission) => (
                  <Col span={12} key={permission.code}>
                    <Checkbox value={permission.code} style={{ width: '100%' }}>
                      <div style={{ marginLeft: 8 }}>
                        <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                          <ShopOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                          {permission.name}
                        </div>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                          {t('dataPermission.code')}: {permission.code}
                        </div>
                      </div>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          )}
        </div>
      </Card>
    );
  };

  // 渲染门店组批量选择
  const renderStoreGroupAuth = () => {
    return (
      <Card>
        <Alert
          message={t('account.storeGroupAuthTip')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {storeGroupLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
            <div style={{ marginTop: 8 }}>{t('account.loadingStoreGroups')}</div>
          </div>
        ) : (
          <>
            <Card title={t('account.selectStoreGroups')} size="small" style={{ marginBottom: 16 }}>
              {/* <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                {t('account.dataStatus')}: {storeGroupLoading ? t('account.loading') : t('account.loadedStoreGroups', { count: storeGroupList.length })}
              </div> */}
              <Select
                mode="multiple"
                placeholder={t('account.selectStoreGroupsPlaceholder')}
                value={selectedStoreGroups}
                onChange={handleStoreGroupSelect}
                style={{ width: '100%' }}
                maxTagCount={3}
                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                loading={storeGroupLoading}
                notFoundContent={storeGroupLoading ? t('account.loadingText') : t('account.noAvailableStoreGroups')}
              >
                {storeGroupList.map(group => (
                  <Select.Option key={group.code} value={group.code}>
                    <Space>
                      <BankOutlined />
                      {group.name}
                      <Tag size="small">{group.shopCodes?.length || 0} {t('account.storeUnit')}</Tag>
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Card>
            
            {selectedStoreGroups.length > 0 && (
              <Card title={t('account.selectedStoreGroupsInfo')} size="small" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>{t('account.selectedStoreGroups')}</div>
                    <Space wrap>
                      {selectedStoreGroups.map(groupCode => {
                        const group = storeGroupList.find(g => g.code === groupCode);
                        return group ? (
                          <Tag 
                            key={groupCode} 
                            color="blue" 
                            closable 
                            onClose={() => {
                              const newGroups = selectedStoreGroups.filter(g => g !== groupCode);
                              handleStoreGroupSelect(newGroups);
                            }}
                          >
                            <BankOutlined /> {group.name} ({group.shopCodes?.length || 0})
                          </Tag>
                        ) : null;
                      })}
                    </Space>
                  </div>
                  
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: 8 }}>{t('account.includedStorePermissions')}</div>
                    <div style={{ 
                      maxHeight: 300, 
                      overflowY: 'auto', 
                      border: '1px solid #f0f0f0', 
                      padding: 12, 
                      borderRadius: 4,
                      backgroundColor: '#fafafa'
                    }}>
                      {selectedStoreGroups.length > 0 ? (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {selectedStoreGroups.map(groupCode => {
                            const group = storeGroupList.find(g => g.code === groupCode);
                            if (!group || !group.shopCodes) return null;
                            
                            return (
                              <div key={groupCode}>
                                <div style={{ color: '#1890ff', fontWeight: 500, fontSize: 13, marginBottom: 4 }}>
                                  {group.name}：
                                </div>
                                <div style={{ marginLeft: 16 }}>
                                  {group.shopCodes.map(shopCode => {
                                    const permissionCode = mapStoreToPermission(shopCode);
                                    const permission = storePermissionList.find(p => p.code === permissionCode);
                                    const isSelected = checkedKeys.includes(permissionCode);
                                    
                                    return (
                                      <div key={shopCode} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        marginBottom: 6,
                                        fontSize: 12,
                                        padding: '4px 8px',
                                        backgroundColor: isSelected ? '#f6ffed' : '#fff',
                                        border: isSelected ? '1px solid #b7eb8f' : '1px solid #e8e8e8',
                                        borderRadius: 4
                                      }}>
                                        <CheckOutlined 
                                          style={{ 
                                            color: isSelected ? '#52c41a' : '#d9d9d9',
                                            marginRight: 8,
                                            fontSize: 10
                                          }} 
                                        />
                                        <span style={{ 
                                          color: isSelected ? '#52c41a' : '#666',
                                          flex: 1
                                        }}>
                                          {permission ? permission.name : t('account.storeDataPermission', { shopCode })}
                                        </span>
                                        <span style={{ color: '#999', fontSize: 11 }}>
                                          ({permissionCode || shopCode})
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </Space>
                      ) : (
                        <div style={{ color: '#999', fontSize: 12, textAlign: 'center', padding: 20 }}>
                          {t('account.selectStoreGroupToView')}
                        </div>
                      )}
                    </div>
                  </div>
                </Space>
              </Card>
            )}
          </>
        )}
      </Card>
    );
  };

  // Check handler for checkbox group
  const onCheck = (checkedValues) => {
    setCheckedKeys(checkedValues);
  };

  // Select all store permissions
  const selectAll = () => {
    const allKeys = storePermissionList.map(permission => permission.code);
    setCheckedKeys(allKeys);
  };

  // Clear all store permissions
  const clearAll = () => {
    setCheckedKeys([]);
  };

  // Check if there are changes
  const hasChanges = () => {
    return JSON.stringify(checkedKeys.sort()) !== JSON.stringify(originalCheckedKeys.sort());
  };

  // Calculate change statistics
  const getChangeStats = () => {
    const addedPermissions = checkedKeys.filter(key => !originalCheckedKeys.includes(key));
    const removedPermissions = originalCheckedKeys.filter(key => !checkedKeys.includes(key));
    const totalChanges = addedPermissions.length + removedPermissions.length;
    
    return {
      added: addedPermissions.length,
      removed: removedPermissions.length,
      total: totalChanges,
      hasChanges: totalChanges > 0
    };
  };

  // Save store permission assignments
  const handleSave = async () => {
    if (!loginId) {
      console.error('账号信息不完整:', loginId);
      message.error(t('account.accountInfoIncomplete'));
      return;
    }
    
    if (!getChangeStats().hasChanges) {
      message.info(t('account.noChangesToSave'));
      return;
    }
    
    // 计算新增和移除的权限
    const originalSet = new Set(originalCheckedKeys);
    const currentSet = new Set(checkedKeys);
    
    // 找出新增的权限（在当前选中但不在原始列表中）
    const addedPermissions = checkedKeys.filter(code => !originalSet.has(code));
    
    // 找出移除的权限（在原始列表但不在当前选中中）
    const removedPermissions = originalCheckedKeys.filter(code => !currentSet.has(code));
    
    console.log('门店权限变更详情:', {
      loginId: loginId,
      addedPermissions: addedPermissions,
      removedPermissions: removedPermissions,
      originalPermissionCodes: originalCheckedKeys,
      currentPermissionCodes: checkedKeys
    });
    
    setSaving(true);
    try {
      // 处理新增的权限
      if (addedPermissions.length > 0) {
        const addResponse = await accountService.assignRoles({
          loginId: loginId,
          roleCodes: addedPermissions
        });
        
        if (!addResponse || !addResponse.success) {
          console.error('新增门店权限分配失败:', addResponse);
          message.error(addResponse?.errorMessage || t('account.assignStorePermissionsFailed'));
          return;
        }
      }
      
      // 处理移除的权限
      if (removedPermissions.length > 0) {
        const removeResponse = await accountService.removeRoles({
          loginId: loginId,
          removeRoleCodeSet: removedPermissions
        });
        
        if (!removeResponse || !removeResponse.success) {
          console.error('移除角色分配失败:', removeResponse);
          message.error(removeResponse?.errorMessage || t('account.removeRolesFailed'));
          return;
        }
      }
      
      message.success(t('account.assignStorePermissionsSuccess'));
      // 更新原始状态
      setOriginalCheckedKeys([...checkedKeys]);
    } catch (error) {
      console.error('保存门店权限分配失败:', error);
      message.error(t('account.assignStorePermissionsFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Go back to account management
  const handleGoBack = () => {
    navigate('/account-management');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          {t('account.loadingStorePermissions')}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* 页面头部 */}
      <div style={{ marginBottom: 24 }}>
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>
            <a onClick={handleGoBack} style={{ color: '#1890ff' }}>
              {t('account.title')}
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{t('account.storePermissionAuthorization')}</Breadcrumb.Item>
        </Breadcrumb>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleGoBack}
              style={{ marginRight: 16 }}
            >
              {t('common.back')}
            </Button>
            <div>
              <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {t('account.storePermissionAuthorizationTitle', { accountName: account?.name || loginId })}
              </Title>
              <Text type="secondary">
                {t('account.storePermissionAuthorizationDesc')}
              </Text>
            </div>
          </div>
          
          <Space>
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              disabled={!getChangeStats().hasChanges}
              size="large"
            >
              {t('common.save')}
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={24}>
        <Col span={24}>
          {/* 当前权限显示区域 */}
          <Card 
            title={
              <Space>
                <ShopOutlined style={{ color: '#1890ff' }} />
                {t('account.currentStorePermissions')}
              </Space>
            }
            style={{ marginBottom: 24 }}
            extra={
              <Space>
                <Badge count={originalCheckedKeys.length} showZero color="#52c41a" />
                <span style={{ fontSize: 12, color: '#666' }}>
                  {t('account.totalPermissions', { count: originalCheckedKeys.length })}
                </span>
              </Space>
            }
          >
            {originalCheckedKeys.length === 0 ? (
              <Empty 
                description={t('account.noCurrentStorePermissions')} 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ margin: '40px 0' }}
              />
            ) : (
              <div style={{ 
                maxHeight: 200, 
                overflowY: 'auto',
                padding: '8px 0'
              }}>
                <Row gutter={[12, 12]}>
                  {originalCheckedKeys.map(permissionCode => {
                    const permission = storePermissionList.find(p => p.code === permissionCode);
                    const isStillSelected = checkedKeys.includes(permissionCode);
                    
                    return (
                      <Col span={12} key={permissionCode}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: 6,
                          backgroundColor: isStillSelected ? '#f6ffed' : '#fff2e8',
                          border: isStillSelected ? '1px solid #b7eb8f' : '1px solid #ffd591',
                          fontSize: 13
                        }}>
                          <div style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: isStillSelected ? '#52c41a' : '#fa8c16',
                            marginRight: 10,
                            flexShrink: 0
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: 500, 
                              color: isStillSelected ? '#52c41a' : '#fa8c16'
                            }}>
                              {permission ? permission.name : permissionCode}
                            </div>
                            <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>
                              {t('dataPermission.code')}: {permissionCode}
                            </div>
                          </div>
                          <div style={{ 
                            marginLeft: 8,
                            fontSize: 12,
                            color: isStillSelected ? '#52c41a' : '#fa8c16',
                            flexShrink: 0
                          }}>
                            {isStillSelected ? (
                              <Tooltip title={t('account.permissionKept')}>
                                <CheckOutlined />
                              </Tooltip>
                            ) : (
                              <Tooltip title={t('account.permissionWillBeRemoved')}>
                                <CloseOutlined />
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}
          </Card>

          {/* 操作统计 */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={24}>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Badge count={checkedKeys.length} showZero color="#52c41a" style={{ fontSize: 16 }} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>
                    {t('account.selectedStorePermissionCount', { count: checkedKeys.length })}
                  </div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Badge count={storePermissionList.length} showZero color="#1890ff" style={{ fontSize: 16 }} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>
                    {t('account.totalStorePermissionCount', { count: storePermissionList.length })}
                  </div>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Badge count={getChangeStats().total} showZero color={getChangeStats().hasChanges ? "#fa8c16" : "#52c41a"} style={{ fontSize: 16 }} />
                  <div style={{ marginTop: 8, fontWeight: 500 }}>
                    {getChangeStats().hasChanges ? t('account.hasChanges') : t('account.noChanges')}
                  </div>
                  {getChangeStats().hasChanges && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      +{getChangeStats().added} -{getChangeStats().removed}
                    </div>
                  )}
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center' }}>
                  <Space>
                    <Button onClick={selectAll} disabled={saving}>
                      {t('account.selectAll')}
                    </Button>
                    <Button onClick={clearAll} disabled={saving}>
                      {t('account.clearAll')}
                    </Button>
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>

          {/* 变更提示 */}
          {getChangeStats().hasChanges && (
            <Alert
              message={
                <div>
                  <div>{t('account.storePermissionsChanged')}</div>
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    {getChangeStats().added > 0 && (
                      <span style={{ color: '#52c41a' }}>
                        {t('account.addedPermissions', { count: getChangeStats().added })}
                      </span>
                    )}
                    {getChangeStats().added > 0 && getChangeStats().removed > 0 && (
                      <span style={{ margin: '0 8px', color: '#999' }}>|</span>
                    )}
                    {getChangeStats().removed > 0 && (
                      <span style={{ color: '#fa8c16' }}>
                        {t('account.removedPermissions', { count: getChangeStats().removed })}
                      </span>
                    )}
                  </div>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {/* 权限分配选项卡 */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            items={[
              {
                key: 'individual',
                label: (
                  <Space>
                    <ShopOutlined />
                    {t('account.individualStoreAuth')}
                  </Space>
                ),
                children: renderIndividualStoreAuth()
              },
              {
                key: 'group',
                label: (
                  <Space>
                    <BankOutlined />
                    {t('account.storeGroupAuth')}
                  </Space>
                ),
                children: renderStoreGroupAuth()
              }
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default AccountStoreAuthPage;